import React from 'react';
import { Activity, Calendar, Bell, Users, CheckSquare, AlertCircle, Clock } from 'lucide-react';

/**
 * CaregiverDashboard Component
 * 
 * A visual representation of a caregiver dashboard showing medication management for family members
 * This is a UI mockup for presentation purposes on the Caregivers page
 * Follows mobile-first responsive design principles
 * Includes proper accessibility attributes and semantic HTML
 * 
 * @returns {JSX.Element} A dashboard mockup component
 */
const CaregiverDashboard: React.FC = () => {
  return (
    <div className="relative mt-8" role="img" aria-label="Caregiver Dashboard Preview - Upcoming Plus Feature">
      {/* Upcoming Plus Feature Banner - Outside and above the dashboard */}
      <div className="absolute -top-6 left-0 right-0 z-20 bg-gradient-to-r from-purple-600 to-purple-500 px-3 py-2 rounded-t-lg flex justify-center shadow-sm">
        <span className="text-white text-sm font-medium">Upcoming Plus Feature</span>
      </div>
      
      {/* Main Dashboard Container */}
      <div className="relative bg-white rounded-xl shadow-lg border border-purple-200 p-4 md:p-6 overflow-hidden">
        {/* Translucent Overlay with Backdrop Blur */}
        <div className="absolute inset-0 z-10 backdrop-blur-sm bg-white/15 flex flex-col items-center justify-center">
          <div className="text-center">
            <p className="text-2xl font-semibold text-purple-700 mb-2">Coming Soon</p>
          </div>
        </div>
        
        {/* Dashboard Content - Reduced Opacity */}
        <div className="opacity-[0.85]">
          {/* Dashboard Header */}
          <div className="flex items-center justify-between mb-6">
            <div>
              <h3 className="text-lg font-bold text-gray-900">Caregiver Dashboard</h3>
              <p className="text-sm text-gray-500">Managing medications for 2 family members</p>
            </div>
            <div className="flex space-x-3">
              <button aria-label="Notifications" className="p-2 rounded-full bg-purple-50 text-purple-600 hover:bg-purple-100 transition-colors">
                <Bell size={18} />
              </button>
              <button aria-label="User profiles" className="p-2 rounded-full bg-purple-50 text-purple-600 hover:bg-purple-100 transition-colors">
                <Users size={18} />
              </button>
            </div>
          </div>

          {/* Family Member Tabs */}
          <div className="flex border-b border-gray-200 mb-5">
            <button className="px-4 py-2 font-medium text-purple-600 border-b-2 border-purple-600" aria-selected="true">
              Mom
            </button>
            <button className="px-4 py-2 font-medium text-gray-500 hover:text-gray-700" aria-selected="false">
              Dad
            </button>
            <button className="ml-auto p-1 text-sm text-purple-600 flex items-center">
              <span className="mr-1">Add</span> <span className="text-xs">+</span>
            </button>
          </div>

          {/* Medication Schedule */}
          <div className="mb-6">
            <div className="flex items-center justify-between mb-4">
              <h4 className="font-medium text-gray-800">Today's Medications</h4>
              <button className="text-sm text-purple-600">View all</button>
            </div>

            <div className="space-y-3">
              {/* Morning Medication */}
              <div className="p-3 bg-green-50 border border-green-100 rounded-lg">
                <div className="flex justify-between items-start">
                  <div>
                    <div className="flex items-center mb-1">
                      <span className="bg-green-100 text-green-800 text-xs px-2 py-0.5 rounded-full mr-2">8:00 AM</span>
                      <CheckSquare size={16} className="text-green-600 mr-1" />
                      <span className="text-green-800 text-xs">Taken</span>
                    </div>
                    <p className="font-medium">Lisinopril 10mg</p>
                    <p className="text-sm text-gray-600">Blood pressure medication</p>
                  </div>
                  <span className="bg-white p-1.5 rounded-full shadow-sm">
                    <Activity size={16} className="text-green-600" />
                  </span>
                </div>
              </div>

              {/* Afternoon Medication */}
              <div className="p-3 bg-purple-50 border border-purple-100 rounded-lg">
                <div className="flex justify-between items-start">
                  <div>
                    <div className="flex items-center mb-1">
                      <span className="bg-purple-100 text-purple-800 text-xs px-2 py-0.5 rounded-full mr-2">1:00 PM</span>
                      <Calendar size={16} className="text-purple-600 mr-1" />
                      <span className="text-purple-800 text-xs">Due in 2 hours</span>
                    </div>
                    <p className="font-medium">Metformin 500mg</p>
                    <p className="text-sm text-gray-600">Diabetes management</p>
                  </div>
                  <span className="bg-white p-1.5 rounded-full shadow-sm">
                    <Activity size={16} className="text-purple-600" />
                  </span>
                </div>
              </div>

              {/* Evening Medication - Missed */}
              <div className="p-3 bg-red-50 border border-red-100 rounded-lg">
                <div className="flex justify-between items-start">
                  <div>
                    <div className="flex items-center mb-1">
                      <span className="bg-red-100 text-red-800 text-xs px-2 py-0.5 rounded-full mr-2">8:00 PM</span>
                      <AlertCircle size={16} className="text-red-600 mr-1" />
                      <span className="text-red-800 text-xs">Missed yesterday</span>
                    </div>
                    <p className="font-medium">Simvastatin 20mg</p>
                    <p className="text-sm text-gray-600">Cholesterol medication</p>
                  </div>
                  <span className="bg-white p-1.5 rounded-full shadow-sm">
                    <Activity size={16} className="text-red-600" />
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Quick Actions */}
          <div className="flex justify-between items-center">
            <button className="text-sm font-medium text-purple-600 border border-purple-200 rounded-lg px-3 py-2 hover:bg-purple-50 transition-colors flex items-center">
              <Bell size={16} className="mr-2" />
              Send Reminder
            </button>
            <button className="text-sm font-medium text-purple-600 border border-purple-200 rounded-lg px-3 py-2 hover:bg-purple-50 transition-colors flex items-center">
              <Calendar size={16} className="mr-2" />
              Schedule Call
            </button>
          </div>

          {/* Upgrade Badge - Using Plus instead of Pro as per app requirements */}
          <div className="absolute top-10 right-3 z-[5]">
            <span className="bg-gradient-to-r from-purple-600 to-indigo-600 text-white text-xs px-2 py-1 rounded-full shadow-sm">
              Meduler Plus
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CaregiverDashboard;
